import React, { useState } from 'react'
import MaterialTable from '@material-table/core'
import { TableCell } from '@mui/material'
import * as UIcons from '@iconscout/react-unicons'
import { withTranslation } from 'react-i18next'
import useSWR from 'swr'
import dayjs from 'dayjs'
import ActionsMenu from '../../shared/ActionsMenu'
import StatusPill from '../../shared/StatusPill'
import EmptyRoles from './EmptyRoles'

export default withTranslation()(function RolesTable({ t, q = '', status = '', onViewDetail, onEdit, onDuplicate, onToggleStatus }) {
  const { data } = useSWR('user-mgmt/roles', { revalidateOnFocus: true })
  const [selectedCount, setSelectedCount] = useState(0)

  const roles = data?.data ?? []
  const isActive = (role) => (role.status ?? 'active') !== 'inactive'

  const filtered = roles.filter((role) => {
    const query = q.trim().toLowerCase()
    if (query && !`${role.name} ${role.display_name}`.toLowerCase().includes(query)) return false
    if (status && (isActive(role) ? 'active' : 'inactive') !== status) return false
    return true
  })

  if (!roles.length) return <EmptyRoles />

  return (
    <div className="w-full bg-white rounded-xl border border-border-lines overflow-hidden">
      <MaterialTable
        columns={[
          { title: t('settings.role_management.columns.role_name'), field: 'display_name', render: (row) => row.display_name ?? row.name },
          { title: t('settings.role_management.columns.description'), field: 'description', render: (row) => row.description ?? '—' },
          { title: t('settings.role_management.columns.users_assigned'), field: 'users_count', render: (row) => row.users_count ?? 0 },
          {
            title: t('settings.role_management.columns.status'),
            field: 'status',
            render: (row) => <StatusPill active={isActive(row)} inactiveText={t('settings.role_management.filters.inactive')} />
          },
          {
            title: t('settings.role_management.columns.last_updated'),
            field: 'updated_at',
            render: (row) => row.updated_at ? dayjs(row.updated_at).format('MMM D, YYYY h:mm A') : '—'
          }
        ]}
        data={filtered}
        actions={[{ position: 'row', tooltip: t('settings.role_management.view_details') }]}
        onSelectionChange={(rows) => setSelectedCount(rows.length)}
        options={{
          selection: true,
          actionsColumnIndex: -1,
          toolbar: false,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          headerStyle: { textTransform: 'uppercase', fontSize: '14px', fontWeight: 600, color: '#05213D', backgroundColor: '#F5F6FA' }
        }}
        localization={{
          header: { actions: '' },
          body: { emptyDataSourceMessage: t('misc.no_records') }
        }}
        components={{
          Pagination: (props) => {
            const totalPages = Math.max(1, Math.ceil(props.count / props.rowsPerPage))
            const currentPage = props.page + 1

            return (
              <TableCell colSpan={props.colSpan}>
                <div className="flex flex-wrap items-center justify-between gap-3 py-1">
                  <span className="text-sm text-secondary">
                    {t('settings.role_management.table.selected', { count: selectedCount, total: props.count })}
                  </span>

                  <div className="flex items-center gap-4">
                    <select
                      value={props.rowsPerPage}
                      onChange={(event) => props.onRowsPerPageChange(event)}
                      className="border border-border-lines rounded-lg px-2 py-1 text-sm text-dark-blue"
                    >
                      {(props.rowsPerPageOptions ?? [10, 20, 50]).map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>

                    <span className="text-sm text-secondary whitespace-nowrap">
                      {t('settings.role_management.table.page_of', { page: currentPage, total: totalPages })}
                    </span>

                    <div className="flex items-center gap-1">
                      <button type="button" onClick={(event) => props.onPageChange(event, 0)} disabled={props.page === 0} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleDoubleLeft size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, props.page - 1)} disabled={props.page === 0} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleLeft size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, props.page + 1)} disabled={currentPage >= totalPages} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleRight size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, totalPages - 1)} disabled={currentPage >= totalPages} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleDoubleRight size={16} className="text-secondary" />
                      </button>
                    </div>
                  </div>
                </div>
              </TableCell>
            )
          },

          Action: ({ data: row }) => (
            <ActionsMenu
              items={[
                { key: 'view', icon: UIcons.UilInfoCircle, label: t('settings.role_management.view_details'), onClick: () => onViewDetail(row) },
                { key: 'edit', icon: UIcons.UilEditAlt, label: t('settings.role_management.edit_role'), onClick: () => onEdit(row) },
                { key: 'duplicate', icon: UIcons.UilCopy, label: t('settings.role_management.duplicate_role'), onClick: () => onDuplicate(row) },
                {
                  key: 'toggle-status',
                  icon: UIcons.UilBan,
                  label: t(isActive(row) ? 'settings.role_management.deactivate' : 'settings.role_management.enable'),
                  danger: isActive(row),
                  onClick: () => onToggleStatus(row)
                }
              ]}
            />
          )
        }}
      />
    </div>
  )
})
